import { Alert, Badge, IconButton } from '@/common'
import cx from 'classnames'
import { motion, AnimatePresence } from 'motion/react'
import { useMemo, useState } from 'react'
import CaretDown from '@/common/components/icons/CaretDown/CaretDown'
import CopyIcon from '@/common/components/icons/Copy/Copy'
import useI18n from '@/shared/hooks/usei18n'
import { Trans } from 'react-i18next'
import toast from 'react-hot-toast'
import { ImportedGoodsGRRFieldNames } from '@/features/cbam-reporting/hooks/importedGoodsGRR'

// Column headers guidelines data
const COLUMN_HEADERS: {
  header: ImportedGoodsGRRFieldNames
  required: boolean
  description: string
}[] = [
  {
    header: ImportedGoodsGRRFieldNames.MRN_NO,
    required: false,
    description:
      'Movement Reference Number (optional). If not available, use any unique imported good identifier.'
  },
  {
    header: ImportedGoodsGRRFieldNames.DATE,
    required: true,
    description: 'Import date'
  },
  {
    header: ImportedGoodsGRRFieldNames.HS_CODE,
    required: true,
    description: 'CN / HS code, 8 or 10 numeric digits'
  },
  {
    header: ImportedGoodsGRRFieldNames.COO,
    required: true,
    description: 'ISO 2-letter country code (non-EU)'
  },
  {
    header: ImportedGoodsGRRFieldNames.NETT,
    required: true,
    description: 'Net mass (numeric) in kg'
  },
  {
    header: ImportedGoodsGRRFieldNames.SELLER,
    required: false,
    description: 'Seller / supplier name'
  },
  {
    header: ImportedGoodsGRRFieldNames.UNIQUE_IMPORT_ID_CBAM,
    required: false,
    description: 'Importer identifier used for automatic matching of the saved importer on the platform'
  },
  {
    header: ImportedGoodsGRRFieldNames.CUSTOMS_RULE,
    required: false,
    description: 'Customs procedure code (two letter e.g. 40)'
  },
  {
    header: ImportedGoodsGRRFieldNames.DESCRIPTION,
    required: false,
    description: 'Description of the goods'
  }
]

type ImportedGoodsUploadGuidelinesProps = {
  isRepresentative: boolean
}

export default function ImportedGoodsUploadGuidelines({
  isRepresentative
}: ImportedGoodsUploadGuidelinesProps) {
  const { t } = useI18n()
  const [isGuidelinesOpen, setIsGuidelinesOpen] = useState(false)
  const visibleColumnHeaders = useMemo(
    () =>
      isRepresentative
        ? COLUMN_HEADERS
        : COLUMN_HEADERS.filter(
            column => column.header !== ImportedGoodsGRRFieldNames.UNIQUE_IMPORT_ID_CBAM
          ),
    [isRepresentative]
  )

  const handleCopyHeader = async (header: string) => {
    try {
      await navigator.clipboard.writeText(header)
      toast.success(t('cbamReporting:importedGoodsUpload.guidelines.copied', { header }))
    } catch {
      toast.error(t('cbamReporting:importedGoodsUpload.guidelines.copyFailed'))
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <button
        onClick={() => setIsGuidelinesOpen(!isGuidelinesOpen)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">
            {t('cbamReporting:importedGoodsUpload.guidelines.title')}
          </span>
        </div>
        <CaretDown
          className={cx('size-6 text-gray-500 transition-transform', {
            'rotate-180': isGuidelinesOpen
          })}
        />
      </button>

      <AnimatePresence>
        {isGuidelinesOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
              <div className="px-4 pb-4 border-t border-gray-200">
              <div className="pt-4 space-y-4">
                <Alert type="info">
                  <div className="space-y-2">
                    <p className="text-sm text-gray-800">

                      <Trans i18nKey="cbamReporting:importedGoodsUpload.guidelines.exactMatchWarning" components={{ strong: <strong /> }} />
                    </p>
                    <p className="text-sm text-gray-800">
                      {t('cbamReporting:importedGoodsUpload.guidelines.additionalColumnsNote')}
                    </p>
                  </div>
                </Alert>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {visibleColumnHeaders.map(column => (
                    <div
                      key={column.header}
                      className="flex items-start justify-between gap-3 p-3 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {column.header}
                          </span>
                          {column.required && (
                            <Badge size="xs" variant="red-stroke">
                              {t('cbamReporting:importedGoodsUpload.guidelines.required')}
                            </Badge>
                          )}
                          {!column.required && (
                            <Badge size="xs" variant="blue-stroke">
                              {t('cbamReporting:importedGoodsUpload.guidelines.optional')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{column.description}</p>
                      </div>
                      <IconButton
                        icon={<CopyIcon />}
                        onClick={() => handleCopyHeader(column.header)}
                        variant="txt-default"
                        sizeType="xs"
                        aria-label={t('cbamReporting:importedGoodsUpload.guidelines.copyHeader', { header: column.header })}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
